// Create or edit a video: /admin/video/new and /admin/video/:id

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { ArrowLeftIcon, SparklesIcon, EyeIcon } from "@heroicons/react/20/solid";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";
import EntityPicker from "../../components/admin/EntityPicker";
import StyleframesEditor, { newItemKey, StyleframeItem } from "../../components/admin/StyleframesEditor";
import FrameExtractor from "../../components/admin/FrameExtractor";
import {
  ChipSelector,
  Field,
  inputClass,
  primaryButton,
  ProgressBar,
  secondaryButton,
  Section,
  StatusBadge,
} from "../../components/admin/ui";
import { db } from "../../utils/firebase";
import { notify } from "../../utils/notify";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { useTaxonomies } from "../../hooks/useTaxonomies";
import { usePageMeta } from "../../hooks/usePageMeta";
import { Entity, Video, VideoStatus } from "../../types/cms";
import { TAXONOMY_LABELS, TaxonomyKey } from "../../lib/taxonomies";
import { mergeSuggestions, suggestFromText, Suggestions, TagSuggestion } from "../../lib/autoTag";
import { suggestStylesFromImages } from "../../lib/localVision";
import {
  brandNamesFromTitle,
  buildDuplicateIndex,
  domainsInText,
  EnrichedVideo,
  enrichVideoLink,
  matchEntity,
} from "../../lib/enrich";
import { fallbackThumbnail, isValidVideoInput, parseVideoSource } from "../../lib/videoSources";
import { uploadMedia } from "../../lib/media";
import { readVideoDuration } from "../../lib/sceneDetect";
import { saveVideo } from "../../lib/videoAdmin";

interface ThumbnailChoice {
  source: "link" | "saved" | "upload" | "styleframe";
  url?: string;
  blob?: Blob;
  preview: string;
}

const EMPTY_SUGGESTIONS: Suggestions = { industries: [], formats: [], styles: [] };

function toDateInput(ts?: Timestamp | null): string {
  if (!ts) return "";
  const d = ts.toDate();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDuration(sec?: number): string {
  if (!sec) return "";
  return `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, "0")}`;
}

function parseDuration(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parts = trimmed.split(":").map(Number);
  if (parts.some(isNaN)) return undefined;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

export default function VideoEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { videos, companies: loadedCompanies, agencies: loadedAgencies } = useVideosWithEntities("admin");
  const { taxonomies } = useTaxonomies();

  const [companies, setCompanies] = useState<Entity[]>([]);
  const [agencies, setAgencies] = useState<Entity[]>([]);
  useEffect(() => setCompanies(loadedCompanies), [loadedCompanies]);
  useEffect(() => setAgencies(loadedAgencies), [loadedAgencies]);

  // ---- Form state ----------------------------------------------------------
  const [loaded, setLoaded] = useState(isNew);
  const [videoURL, setVideoURL] = useState(searchParams.get("url") ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [enterpriseId, setEnterpriseId] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [status, setStatus] = useState<VideoStatus>("draft");
  const [savedStatus, setSavedStatus] = useState<VideoStatus | undefined>();
  const [releasedAt, setReleasedAt] = useState("");
  const [duration, setDuration] = useState("");
  const [thumbnail, setThumbnail] = useState<ThumbnailChoice | null>(null);
  const [styleframes, setStyleframes] = useState<StyleframeItem[]>([]);
  const [videoFile, setVideoFile] = useState<{ url?: string; file?: File } | null>(null);
  const [script, setScript] = useState("");
  const [caseStudy, setCaseStudy] = useState("");
  const [tags, setTags] = useState("");
  const [classes, setClasses] = useState<Record<TaxonomyKey, string[]>>({
    industries: [],
    formats: [],
    styles: [],
  });

  // ---- Assistance state ----------------------------------------------------
  const [enriched, setEnriched] = useState<EnrichedVideo | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [matchHints, setMatchHints] = useState<{ brand?: string; studio?: string }>({});
  const [suggestions, setSuggestions] = useState<Suggestions>(EMPTY_SUGGESTIONS);
  const [visionStatus, setVisionStatus] = useState<string | null>(null);
  const [extractorOpen, setExtractorOpen] = useState(false);
  const [saving, setSaving] = useState<{ progress: number; label: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const autoEnrichedFor = useRef<string | null>(null);

  usePageMeta(isNew ? "Nouvelle vidéo · Admin" : `${title || "Vidéo"} · Admin`);

  // ---- Load existing video --------------------------------------------------
  useEffect(() => {
    if (isNew) return;
    getDoc(doc(db, "videos", id!))
      .then((snap) => {
        if (!snap.exists()) {
          notify({ isSuccess: false, message: "Vidéo introuvable" });
          navigate("/admin", { replace: true });
          return;
        }
        const v = snap.data() as Video;
        setVideoURL(v.videoURL ?? "");
        setTitle(v.title ?? "");
        setDescription(v.description ?? "");
        setEnterpriseId(v.enterpriseId ?? "");
        setAgencyId(v.agencyId ?? "");
        setStatus(v.status ?? "published");
        setSavedStatus(v.status ?? "published");
        setReleasedAt(toDateInput(v.releasedAt));
        setDuration(formatDuration(v.durationSec));
        setThumbnail(v.thumbnailURL ? { source: "saved", url: v.thumbnailURL, preview: v.thumbnailURL } : null);
        setStyleframes((v.styleframesURLs ?? []).map((url) => ({ key: newItemKey(), url, preview: url })));
        setVideoFile(v.videoFileURL ? { url: v.videoFileURL } : null);
        setScript(v.script ?? "");
        setCaseStudy(v.caseStudy ?? "");
        setTags((v.tags ?? []).filter(Boolean).join(", "));
        setClasses({ industries: v.industries ?? [], formats: v.formats ?? [], styles: v.styles ?? [] });
        autoEnrichedFor.current = v.videoURL ?? null;
        setLoaded(true);
      })
      .catch((err) => {
        console.error(err);
        notify({ isSuccess: false, message: "Chargement de la vidéo impossible", info: err.message });
      });
  }, [id, isNew, navigate]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const touch = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setDirty(true);
  };

  // ---- Duplicates -----------------------------------------------------------
  const duplicate = useMemo(() => {
    if (!isValidVideoInput(videoURL)) return null;
    const index = buildDuplicateIndex(videos);
    const existing = index.get(parseVideoSource(videoURL).sourceKey);
    return existing && existing.id !== id ? existing : null;
  }, [videos, videoURL, id]);

  // ---- Enrichment -----------------------------------------------------------
  const runEnrichment = useCallback(
    async (url: string, { overwrite = false } = {}) => {
      if (!isValidVideoInput(url)) return;
      setEnriching(true);
      try {
        const info = await enrichVideoLink(url);
        setEnriched(info);
        // Functional updates: the admin may have typed while the fetch ran.
        const fill = (next: string | undefined, setter: React.Dispatch<React.SetStateAction<string>>) => {
          if (next) setter((current) => (overwrite || !current.trim() ? next : current));
        };
        fill(info.title, setTitle);
        fill(info.description, setDescription);
        if (info.durationSec) fill(formatDuration(info.durationSec), setDuration);
        if (info.releasedAt) fill(toDateInput(Timestamp.fromDate(info.releasedAt)), setReleasedAt);
        if (info.thumbnailURL) {
          const linkThumb: ThumbnailChoice = { source: "link", url: info.thumbnailURL, preview: info.thumbnailURL };
          setThumbnail((current) => (!current || current.source === "link" ? linkThumb : current));
        }

        const domains = domainsInText(info.description);
        const hints: { brand?: string; studio?: string } = {};
        if (!enterpriseId) {
          const brand = matchEntity(companies, {
            names: [...brandNamesFromTitle(info.title), info.authorName],
            domains,
          });
          if (brand) {
            setEnterpriseId((current) => current || brand.entity.id);
            hints.brand = `Trouvée via ${brand.reason}`;
          }
        }
        if (!agencyId) {
          const studio = matchEntity(agencies, { names: [info.authorName], domains });
          if (studio) {
            setAgencyId((current) => current || studio.entity.id);
            hints.studio = `Trouvé via ${studio.reason}`;
          }
        }
        setMatchHints(hints);

        const text = suggestFromText(taxonomies, {
          title: info.title ?? title,
          description: info.description ?? description,
          authorName: info.authorName,
        });
        setSuggestions(text);
        setDirty(true);
        info.warnings.forEach((w) => notify({ isSuccess: false, message: w }));
      } finally {
        setEnriching(false);
      }
    },
    [title, description, enterpriseId, agencyId, companies, agencies, taxonomies],
  );

  // New video opened with ?url=… (bookmarklet, submissions). Typing in the
  // field doesn't trigger it: use paste or the "Récupérer les infos" button.
  const urlParam = searchParams.get("url");
  useEffect(() => {
    if (!isNew || !urlParam || videoURL !== urlParam || autoEnrichedFor.current === videoURL) return;
    if (!companies.length && !agencies.length) return; // wait for catalog to match entities
    autoEnrichedFor.current = videoURL;
    runEnrichment(videoURL);
  }, [isNew, urlParam, videoURL, companies.length, agencies.length, runEnrichment]);

  const suggestFromCurrentText = () => {
    setSuggestions((prev) => {
      const text = suggestFromText(taxonomies, { title, description, authorName: enriched?.authorName, tags: tags.split(",") });
      return {
        industries: mergeSuggestions(prev.industries, text.industries),
        formats: mergeSuggestions(prev.formats, text.formats),
        styles: mergeSuggestions(prev.styles, text.styles),
      };
    });
  };

  const runVision = async () => {
    const images = [
      thumbnail?.preview ?? fallbackThumbnail(videoURL),
      ...styleframes.map((s) => s.preview),
    ].filter((u): u is string => !!u);
    if (!images.length) {
      notify({ isSuccess: false, message: "Ajoutez une miniature ou des styleframes à analyser." });
      return;
    }
    setVisionStatus("Préparation…");
    try {
      const styles = await suggestStylesFromImages(images, taxonomies.styles, setVisionStatus);
      setSuggestions((prev) => ({ ...prev, styles: mergeSuggestions(prev.styles, styles) }));
      notify({
        isSuccess: true,
        message: styles.length ? `Styles suggérés : ${styles.map((s) => s.value).join(", ")}` : "Aucun style ne ressort nettement.",
      });
    } catch (err) {
      console.error(err);
      notify({
        isSuccess: false,
        message: "Analyse visuelle impossible",
        info: err instanceof Error ? err.message : "Le modèle n'a pas pu être chargé. Vérifiez la connexion et réessayez.",
      });
    } finally {
      setVisionStatus(null);
    }
  };

  const acceptAllSuggestions = () => {
    setClasses((prev) => {
      const next = { ...prev };
      (Object.keys(suggestions) as TaxonomyKey[]).forEach((key) => {
        const accepted = suggestions[key].filter((s) => s.confidence >= 0.5).map((s) => s.value);
        next[key] = Array.from(new Set([...prev[key], ...accepted]));
      });
      return next;
    });
    setDirty(true);
  };

  // ---- Thumbnails -----------------------------------------------------------
  const linkThumbnail = enriched?.thumbnailURL ?? fallbackThumbnail(videoURL);
  const thumbnailChoices: ThumbnailChoice[] = [];
  if (linkThumbnail) thumbnailChoices.push({ source: "link", url: linkThumbnail, preview: linkThumbnail });
  if (thumbnail && !thumbnailChoices.some((c) => c.preview === thumbnail.preview)) thumbnailChoices.push(thumbnail);

  // ---- Save -----------------------------------------------------------------
  const validate = (target: VideoStatus) => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = "Le titre est obligatoire.";
    if (!videoURL.trim() && !videoFile) next.videoURL = "Ajoutez un lien ou un fichier vidéo.";
    if (videoURL.trim() && !isValidVideoInput(videoURL)) next.videoURL = "Ce lien n'est pas une URL valide.";
    if (target === "published" && !enterpriseId) next.enterpriseId = "Choisissez la marque avant de publier.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (target: VideoStatus = status) => {
    if (!validate(target)) {
      notify({ isSuccess: false, message: "Il manque des informations", info: "Les champs à compléter sont indiqués en rouge." });
      return;
    }
    if (saving) return;
    setSaving({ progress: 0, label: "Préparation…" });
    const newFrames = styleframes.filter((s) => s.blob).length;
    const steps = newFrames + (thumbnail?.blob ? 1 : 0) + (videoFile?.file ? 1 : 0) + 1;
    let done = 0;
    const step = (label: string) => (ratio: number) => setSaving({ progress: (done + ratio) / steps, label });

    try {
      let videoFileURL = videoFile?.url;
      if (videoFile?.file) {
        videoFileURL = await uploadMedia("videos", videoFile.file, videoFile.file.name, step("Envoi du fichier vidéo…"));
        done++;
      }

      const styleframesURLs: string[] = [];
      for (const item of styleframes) {
        if (item.url) {
          styleframesURLs.push(item.url);
        } else if (item.blob) {
          styleframesURLs.push(await uploadMedia("styleframes", item.blob, item.name ?? title, step(`Envoi des styleframes (${done + 1}/${newFrames})…`)));
          done++;
        }
      }

      let thumbnailURL = thumbnail?.url ?? "";
      if (thumbnail?.blob) {
        thumbnailURL = await uploadMedia("thumbnails", thumbnail.blob, title, step("Envoi de la miniature…"));
        done++;
      }
      if (!thumbnailURL) thumbnailURL = linkThumbnail ?? styleframesURLs[0] ?? "";

      step("Enregistrement…")(0);
      const releaseDate = releasedAt ? new Date(`${releasedAt}T12:00:00`) : null;
      const savedId = await saveVideo(
        isNew ? null : id!,
        {
          title: title.trim(),
          description: description.trim(),
          videoURL: videoURL.trim() ? parseVideoSource(videoURL).url : videoFileURL ?? "",
          videoFileURL: videoFileURL ?? "",
          enterpriseId,
          agencyId,
          status: target,
          releasedAt: releaseDate ? Timestamp.fromDate(releaseDate) : null,
          durationSec: parseDuration(duration),
          thumbnailURL,
          styleframesURLs,
          script,
          caseStudy: caseStudy.trim(),
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
          industries: classes.industries,
          formats: classes.formats,
          styles: classes.styles,
        },
        savedStatus,
      );

      setStatus(target);
      setSavedStatus(target);
      setStyleframes(styleframesURLs.map((url) => ({ key: newItemKey(), url, preview: url })));
      setThumbnail(thumbnailURL ? { source: "saved", url: thumbnailURL, preview: thumbnailURL } : null);
      setVideoFile(videoFileURL ? { url: videoFileURL } : null);
      setDirty(false);
      notify({
        isSuccess: true,
        message: target === "published" ? "Vidéo publiée" : "Brouillon enregistré",
      });
      if (isNew) navigate(`/admin/video/${savedId}`, { replace: true });
    } catch (err) {
      console.error(err);
      notify({
        isSuccess: false,
        message: "Enregistrement impossible",
        info: err instanceof Error ? err.message : "Vérifiez votre connexion puis réessayez.",
      });
    } finally {
      setSaving(null);
    }
  };

  const onEntityCreated = (entity: Entity, kind: "companies" | "agencies") => {
    (kind === "companies" ? setCompanies : setAgencies)((prev) => [...prev, entity]);
  };

  if (!loaded) {
    return (
      <div className="min-h-screen bg-neutral-50 md:px-20 px-8">
        <NavBar />
        <p className="px-4 py-12 text-neutral-500 animate-pulse">Chargement de la vidéo…</p>
      </div>
    );
  }

  const hasSuggestions = (Object.values(suggestions) as TagSuggestion[][]).some((l) => l.length);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 md:px-20 px-4">
      <NavBar />
      <div className="max-w-5xl mx-auto px-0 md:px-4 py-8 flex flex-col gap-6 pb-32">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900">
            <ArrowLeftIcon className="w-4 h-4" /> CMS
          </Link>
          <span className="text-neutral-300">/</span>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {isNew ? "Nouvelle vidéo" : title || "Sans titre"}
          </h1>
          {!isNew && <StatusBadge status={savedStatus} />}
          {!isNew && savedStatus === "published" && (
            <Link to={`/video/${id}`} target="_blank" className="ml-auto inline-flex items-center gap-1 text-sm underline">
              <EyeIcon className="w-4 h-4" /> Voir sur le site
            </Link>
          )}
        </div>

        {/* ---- Source ---- */}
        <Section
          title="Source"
          description="Collez un lien YouTube, Vimeo ou X : titre, miniature, durée, marque, studio et catégories sont proposés automatiquement."
        >
          <Field
            label="Lien de la vidéo"
            htmlFor="video-url"
            error={errors.videoURL}
            hint={enriched?.authorName ? `Chaîne : ${enriched.authorName}` : "Un code d'intégration <iframe> fonctionne aussi."}
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                id="video-url"
                className={`${inputClass} ${errors.videoURL ? "border-red-400" : ""}`}
                value={videoURL}
                placeholder="https://www.youtube.com/watch?v=…"
                onChange={(e) => touch(setVideoURL)(e.target.value)}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData("text");
                  if (isValidVideoInput(pasted)) setTimeout(() => runEnrichment(pasted), 0);
                }}
              />
              <button
                type="button"
                className={secondaryButton + " whitespace-nowrap"}
                disabled={!isValidVideoInput(videoURL) || enriching}
                onClick={() => runEnrichment(videoURL, { overwrite: !isNew })}
              >
                <SparklesIcon className="w-4 h-4" />
                {enriching ? "Récupération…" : isNew ? "Récupérer les infos" : "Mettre à jour depuis le lien"}
              </button>
            </div>
          </Field>
          {duplicate && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Cette vidéo existe déjà :{" "}
              <Link className="font-medium underline" to={`/admin/video/${duplicate.id}`}>
                {duplicate.title || "sans titre"}
              </Link>{" "}
              (<StatusBadge status={duplicate.status} />).
            </div>
          )}

          <Field
            label="Fichier vidéo hébergé (optionnel)"
            hint="Fichier fourni par le studio : active l'aperçu animé au survol sur le site et l'extraction de styleframes. MP4 H.264 conseillé, 1 Go max."
          >
            <div className="flex flex-wrap items-center gap-3">
              {videoFile?.url && !videoFile.file && (
                <video src={videoFile.url} className="h-20 rounded border border-neutral-200" muted preload="metadata" />
              )}
              <label className={secondaryButton + " cursor-pointer"}>
                {videoFile ? "Remplacer le fichier" : "Choisir un fichier"}
                <input
                  type="file"
                  accept="video/*"
                  className="sr-only"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setVideoFile({ file });
                    setDirty(true);
                    const sec = await readVideoDuration(file);
                    if (sec && !duration) setDuration(formatDuration(sec));
                  }}
                />
              </label>
              {videoFile?.file && <span className="text-sm text-neutral-700">{videoFile.file.name} · envoyé à l'enregistrement</span>}
              {videoFile && (
                <button type="button" className="text-sm text-red-700 underline" onClick={() => touch(setVideoFile)(null)}>
                  Retirer
                </button>
              )}
            </div>
          </Field>
        </Section>

        {/* ---- Informations ---- */}
        <Section title="Informations">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Field label="Titre" htmlFor="video-title" error={errors.title}>
                <input
                  id="video-title"
                  className={`${inputClass} ${errors.title ? "border-red-400" : ""}`}
                  value={title}
                  onChange={(e) => touch(setTitle)(e.target.value)}
                />
              </Field>
            </div>
            <EntityPicker
              label="Marque (Video for)"
              kind="companies"
              companies={companies}
              agencies={agencies}
              value={enterpriseId}
              onChange={touch(setEnterpriseId)}
              onCreated={onEntityCreated}
              hint={matchHints.brand}
              suggestedName={brandNamesFromTitle(title)[0]}
              error={errors.enterpriseId}
            />
            <EntityPicker
              label="Studio (Produced by)"
              kind="agencies"
              companies={companies}
              agencies={agencies}
              value={agencyId}
              onChange={touch(setAgencyId)}
              onCreated={onEntityCreated}
              hint={matchHints.studio ?? "Laisser vide si la vidéo est réalisée en interne."}
              suggestedName={enriched?.authorName}
            />
            <div className="md:col-span-2">
              <Field label="Description" htmlFor="video-desc" hint="Utilisée pour le référencement et les aperçus de partage (≈ 160 caractères idéalement).">
                <textarea
                  id="video-desc"
                  rows={3}
                  className={inputClass}
                  value={description}
                  onChange={(e) => touch(setDescription)(e.target.value)}
                />
              </Field>
            </div>
            <Field label="Date de sortie" htmlFor="video-date">
              <input id="video-date" type="date" className={inputClass} value={releasedAt} onChange={(e) => touch(setReleasedAt)(e.target.value)} />
            </Field>
            <Field label="Durée" htmlFor="video-duration" hint="Format minutes:secondes, ex. 1:24">
              <input id="video-duration" className={inputClass} value={duration} placeholder="1:24" onChange={(e) => touch(setDuration)(e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* ---- Visuels ---- */}
        <Section title="Visuels">
          <Field label="Miniature">
            <div className="flex flex-wrap gap-3">
              {thumbnailChoices.map((choice) => {
                const on = thumbnail?.preview === choice.preview || (!thumbnail && choice.source === "link");
                return (
                  <button
                    type="button"
                    key={choice.preview}
                    onClick={() => touch(setThumbnail)(choice)}
                    className={`relative w-48 rounded-md overflow-hidden border-2 ${on ? "border-neutral-900" : "border-transparent"}`}
                  >
                    <img src={choice.preview} alt="" className="w-full aspect-video object-cover bg-neutral-100" />
                    <span className="absolute bottom-1 left-1 text-[11px] bg-black/70 text-white px-1.5 py-0.5 rounded">
                      {{ link: "Depuis le lien", saved: "Actuelle", upload: "Importée", styleframe: "Styleframe" }[choice.source]}
                    </span>
                  </button>
                );
              })}
              <label className="w-48 aspect-video rounded-md border border-dashed border-neutral-300 flex items-center justify-center text-sm text-neutral-600 cursor-pointer hover:border-neutral-500">
                Importer une image
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) touch(setThumbnail)({ source: "upload", blob: f, preview: URL.createObjectURL(f) });
                  }}
                />
              </label>
            </div>
          </Field>

          <Field label="Styleframes" hint="Les images sont converties en WebP et redimensionnées automatiquement à l'envoi.">
            <StyleframesEditor
              items={styleframes}
              onChange={touch(setStyleframes)}
              onExtract={() => setExtractorOpen(true)}
              onUseAsThumbnail={(item) =>
                touch(setThumbnail)({ source: "styleframe", url: item.url, blob: item.blob, preview: item.preview })
              }
            />
          </Field>
        </Section>

        {/* ---- Classification ---- */}
        <Section
          title="Classification"
          description={
            <>
              Les suggestions en <span className="text-violet-800 font-medium">violet pointillé</span> sont
              calculées localement (texte du titre et de la description, analyse visuelle dans votre
              navigateur). Cliquez pour les accepter.
            </>
          }
          actions={
            <>
              <button type="button" className={secondaryButton} onClick={suggestFromCurrentText}>
                Suggérer depuis le texte
              </button>
              <button type="button" className={secondaryButton} onClick={runVision} disabled={!!visionStatus}>
                <SparklesIcon className="w-4 h-4" />
                {visionStatus ?? "Analyser les visuels"}
              </button>
              {hasSuggestions && (
                <button type="button" className={primaryButton} onClick={acceptAllSuggestions}>
                  Accepter les suggestions sûres
                </button>
              )}
            </>
          }
        >
          {(Object.keys(TAXONOMY_LABELS) as TaxonomyKey[]).map((key) => (
            <Field key={key} label={TAXONOMY_LABELS[key]}>
              <ChipSelector
                options={taxonomies[key]}
                selected={classes[key]}
                suggestions={suggestions[key]}
                onChange={(next) => {
                  setClasses((prev) => ({ ...prev, [key]: next }));
                  setDirty(true);
                }}
              />
            </Field>
          ))}
          <Field label="Tags" htmlFor="video-tags" hint="Séparés par des virgules.">
            <input id="video-tags" className={inputClass} value={tags} onChange={(e) => touch(setTags)(e.target.value)} />
          </Field>
        </Section>

        {/* ---- Contenu ---- */}
        <Section title="Contenu complémentaire">
          <Field label="Script" htmlFor="video-script">
            <textarea id="video-script" rows={6} className={inputClass} value={script} onChange={(e) => touch(setScript)(e.target.value)} />
          </Field>
          <Field label="Case study (lien)" htmlFor="video-case">
            <input id="video-case" className={inputClass} value={caseStudy} placeholder="https://…" onChange={(e) => touch(setCaseStudy)(e.target.value)} />
          </Field>
        </Section>
      </div>

      {/* ---- Save bar ---- */}
      <div className="fixed bottom-0 inset-x-0 z-[2000] border-t border-neutral-200 bg-white/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
          {saving ? (
            <div className="flex-1 min-w-[200px]">
              <ProgressBar value={saving.progress} label={saving.label} />
            </div>
          ) : (
            <span className="flex-1 text-sm text-neutral-500">
              {dirty ? "Modifications non enregistrées" : savedStatus ? "Tout est enregistré" : ""}
            </span>
          )}
          {savedStatus === "published" ? (
            <>
              <button type="button" className={secondaryButton} disabled={!!saving} onClick={() => save("draft")}>
                Dépublier
              </button>
              <button type="button" className={primaryButton} disabled={!!saving} onClick={() => save("published")}>
                Enregistrer
              </button>
            </>
          ) : (
            <>
              <button type="button" className={secondaryButton} disabled={!!saving} onClick={() => save("draft")}>
                Enregistrer le brouillon
              </button>
              <button type="button" className={primaryButton} disabled={!!saving} onClick={() => save("published")}>
                Publier
              </button>
            </>
          )}
        </div>
      </div>

      <FrameExtractor
        open={extractorOpen}
        onClose={() => setExtractorOpen(false)}
        source={videoFile?.file ?? videoFile?.url ?? null}
        onConfirm={(frames) => {
          setStyleframes((prev) => [
            ...prev,
            ...frames.map((f) => ({ key: newItemKey(), blob: f.blob, name: f.name, preview: f.previewURL })),
          ]);
          setDirty(true);
        }}
      />
      <Footer />
    </div>
  );
}
